/**
 * Agent Overview / Configuration Page
 *
 * Full agent configuration with internal sidebar navigation.
 * Sections: General, Behavior, Intelligence, Tools, Skills, Advanced.
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Save, Zap, Check, Loader2, Sparkles,
  User, MessageSquareText, Brain, Wrench, Blocks, SlidersHorizontal,
  ChevronRight,
} from 'lucide-react'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Textarea } from '../../../components/ui/Textarea'
import { Select } from '../../../components/ui/Select'
import { Separator } from '../../../components/ui/Separator'
import { Switch } from '../../../components/ui/switch'
import { ModelSelector } from '../../../components/ui/ModelSelector'
import { MetadataConfig } from '../../../components/features/chatbot/MetadataConfig'
import { VariablesSection } from '../../../components/features/chatbot/VariablesSection'
import { VariablesDisplay } from '../../../components/features/chatbot/VariablesDisplay'
import { ToolsTab } from '../AgentHubTabs/ToolsTab'
import { SkillsTab } from '../AgentHubTabs/SkillsTab'
import {
  TEMPLATE_OPTIONS,
  TEMPLATE_TEXTS,
  DEFAULT_STYLE_TONE,
  SEARCH_THRESHOLD_OPTIONS,
  TEMPERATURE_OPTIONS,
  FALLBACK_STRATEGY_OPTIONS,
  KNOWLEDGE_MODE_OPTIONS,

} from '../../../components/chatbot/behaviorTemplates'
import { useChatbotStore } from '../../../stores/chatbotStore'
import { useToast } from '../../../hooks/useToast'
import type { CreateChatbotRequest } from '../../../types'
import api from '../../../services/api'
import { cn } from '../../../utils/cn'

type SettingsSection = 'general' | 'behavior' | 'intelligence' | 'tools' | 'skills' | 'advanced'

interface SectionNavItem {
  key: SettingsSection
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

const SECTIONS: SectionNavItem[] = [
  { key: 'general', label: 'General', description: 'Identity & model', icon: User },
  { key: 'behavior', label: 'Behavior', description: 'Personality & prompts', icon: MessageSquareText },
  { key: 'intelligence', label: 'Intelligence', description: 'Search & response tuning', icon: Brain },
  { key: 'tools', label: 'Tools', description: 'External API actions', icon: Wrench },
  { key: 'skills', label: 'Skills', description: 'Agent capabilities', icon: Blocks },
  { key: 'advanced', label: 'Advanced', description: 'Metadata & variables', icon: SlidersHorizontal },
]

const SECTION_HEADERS: Record<SettingsSection, { title: string; subtitle: string }> = {
  general: { title: 'General', subtitle: 'Basic agent identity and model configuration' },
  behavior: { title: 'Behavior', subtitle: "Define your agent's personality and instructions" },
  intelligence: { title: 'Intelligence', subtitle: 'Fine-tune how your agent searches and responds' },
  tools: { title: 'Tools', subtitle: 'Connect external tools and API actions' },
  skills: { title: 'Skills', subtitle: 'Manage agent skills and capabilities' },
  advanced: { title: 'Advanced', subtitle: 'Metadata fields and variable configuration' },
}

export const AgentSettingsPage: React.FC = () => {
  const { chatbot, refetch } = useAgentContext()
  const [activeSection, setActiveSection] = useState<SettingsSection>('general')
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [availableModels, setAvailableModels] = useState<Array<Record<string, unknown>>>([])
  const [loadingModels, setLoadingModels] = useState(false)

  const { updateChatbot, isUpdating } = useChatbotStore()
  const { toast } = useToast()

  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true)
      try {
        const response = await api.get('/llm-models/active')
        setAvailableModels(response.data.models || [])
      } catch {
        setAvailableModels([
          { value: 'openai/gpt-4o-mini', label: 'OPENAI - gpt-4o-mini', credit_cost: 10 },
          { value: 'openai/gpt-4o', label: 'OPENAI - gpt-4o', credit_cost: 50 },
        ])
      } finally {
        setLoadingModels(false)
      }
    }
    loadModels()
  }, [])

  useEffect(() => {
    const s = chatbot as Record<string, unknown>
    const settings = (s.settings || {}) as Record<string, unknown>
    setFormData({
      name: chatbot.name || '',
      description: chatbot.description || '',
      ai_model: chatbot.ai_model || 'openai/gpt-4o-mini',
      tone: chatbot.tone || (s.conversation_tone as string) || 'professional',
      personality: chatbot.personality || (s.personality_traits as string) || 'helpful',
      response_style: chatbot.response_style || 'detailed',
      technical_level: chatbot.technical_level || 'beginner',
      search_threshold: chatbot.search_threshold || 0.5,
      temperature: (s.temperature as number) ?? 0.1,
      fallback_strategy: 'clarify_first',
      allow_handoff: (s.allow_handoff as boolean) ?? false,
      prompt_template_key: s.prompt_template_key as string,
      owner_prompt: s.owner_prompt as string,
      prompt_mode: (s.prompt_mode as string) || 'append',
      settings: {
        ...settings,
        agent_runtime: (settings.agent_runtime as string) || 'pydanticai',
        style_tone_instructions: (settings.style_tone_instructions as string) ?? DEFAULT_STYLE_TONE,
      },
      metadata_schema: (s.metadata_schema as Record<string, unknown>) || { fields: [] },
    })
    setErrors({})
  }, [chatbot])

  const updateFormField = useCallback((updates: Record<string, unknown>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    setErrors(prev => {
      const next = { ...prev }
      Object.keys(updates).forEach(key => delete next[key])
      return next
    })
  }, [])

  const handleTemplateSelect = (key: string) => {
    if (key === 'custom') updateFormField({ prompt_template_key: key, owner_prompt: '' })
    else updateFormField({ prompt_template_key: key, owner_prompt: TEMPLATE_TEXTS[key] || '' })
  }

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}
    if (!(formData.name as string)?.trim()) newErrors.name = 'Name is required'
    if ((formData.owner_prompt as string)?.length > 10000) newErrors.owner_prompt = 'Prompt exceeds 10,000 characters'
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    try {
      const payload: Partial<CreateChatbotRequest> = {
        name: formData.name as string,
        description: (formData.description as string) || '',
        ai_model: (formData.ai_model as string) || 'openai/gpt-4o-mini',
        bot_type: 'custom',
        tone: (formData.tone as string) || 'professional',
        personality: (formData.personality as string) || 'helpful',
        response_style: (formData.response_style as string) || 'detailed',
        technical_level: (formData.technical_level as string) || 'beginner',
        search_threshold: (formData.search_threshold as number) ?? 0.5,
        temperature: (formData.temperature as number) ?? 0.1,
        fallback_strategy: (formData.fallback_strategy as string) || 'clarify_first',
        allow_handoff: (formData.allow_handoff as boolean) ?? false,
        prompt_template_key: formData.prompt_template_key as string,
        owner_prompt: formData.owner_prompt as string,
        prompt_mode: formData.prompt_mode as CreateChatbotRequest['prompt_mode'],
        settings: formData.settings as CreateChatbotRequest['settings'],
        metadata_schema: formData.metadata_schema as CreateChatbotRequest['metadata_schema'],
      }
      await updateChatbot(chatbot.id, payload)
      toast({ title: 'Changes saved', variant: 'default' })
      refetch()
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to save' })
    }
  }

  const promptLen = (formData.owner_prompt as string)?.length || 0
  const promptPct = promptLen / 10000
  const sectionInfo = SECTION_HEADERS[activeSection]
  const isFormSection = ['general', 'behavior', 'intelligence', 'advanced'].includes(activeSection)
  const formSettings = formData.settings as Record<string, unknown> | undefined

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col lg:flex-row lg:gap-8">
        {/* ── Sidebar nav ── */}
        <nav className="w-60 shrink-0 hidden lg:block">
          <div className="sticky top-6 space-y-1">
            {SECTIONS.map((section) => {
              const active = activeSection === section.key
              return (
                <button
                  key={section.key}
                  onClick={() => setActiveSection(section.key)}
                  className={cn(
                    'group flex items-center gap-3 w-full rounded-xl px-3.5 py-3 text-left transition-all duration-150',
                    active
                      ? 'bg-primary/[0.08] text-foreground shadow-sm ring-1 ring-primary/20'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <div className={cn(
                    'flex size-8 items-center justify-center rounded-lg transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/80 text-muted-foreground group-hover:bg-muted group-hover:text-foreground',
                  )}>
                    <section.icon className="size-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight">{section.label}</div>
                    <div className={cn(
                      'text-xs leading-tight mt-0.5 transition-colors',
                      active ? 'text-muted-foreground' : 'text-muted-foreground/60',
                    )}>
                      {section.description}
                    </div>
                  </div>
                  <ChevronRight className={cn(
                    'size-3.5 shrink-0 transition-all',
                    active ? 'text-primary opacity-100' : 'opacity-0 group-hover:opacity-40',
                  )} />
                </button>
              )
            })}
          </div>
        </nav>

        {/* Mobile section selector */}
        <div className="lg:hidden w-full mb-4">
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
            {SECTIONS.map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors shrink-0',
                  activeSection === section.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                <section.icon className="size-3.5" />
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content area ── */}
        <div className="flex-1 min-w-0">
          {/* Section header */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold tracking-tight text-foreground">{sectionInfo.title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{sectionInfo.subtitle}</p>
          </div>

          {/* Section content */}
          <div className="space-y-6">
            {activeSection === 'general' && (
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    value={(formData.name as string) || ''}
                    onChange={e => updateFormField({ name: e.target.value })}
                    placeholder="e.g., Product Support Agent"
                    error={errors.name}
                    className="max-w-lg"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Description</label>
                  <Textarea
                    value={(formData.description as string) || ''}
                    onChange={e => updateFormField({ description: e.target.value })}
                    placeholder="What does this agent do?"
                    rows={3}
                  />
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    AI Model
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">Choose the AI model that powers this agent.</p>
                  <ModelSelector
                    models={availableModels}
                    value={(formData.ai_model as string) || 'openai/gpt-4o-mini'}
                    onChange={v => {
                      updateFormField({ ai_model: v })
                      // Kimi models only accept temperature=1.0
                      if (v.startsWith('moonshotai/')) updateFormField({ temperature: 1.0 })
                    }}
                    disabled={loadingModels}
                    loading={loadingModels}
                  />
                </div>

                {(() => {
                  const selected = availableModels.find(m => m.value === formData.ai_model)
                  return selected?.supports_thinking ? (
                    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                      <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                      <p className="text-sm text-foreground">
                        This model supports <span className="font-semibold">extended thinking</span> for deeper reasoning.
                      </p>
                    </div>
                  ) : null
                })()}
              </div>
            )}

            {activeSection === 'behavior' && (
              <div className="space-y-8">
                {/* Template picker */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-foreground">Behavior Template</label>
                    <p className="text-xs text-muted-foreground mt-0.5">Pick a starting template or write custom instructions.</p>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {TEMPLATE_OPTIONS.map(tpl => {
                      const selected = ((formData.prompt_template_key as string) || 'base_instructions') === tpl.key
                      return (
                        <button
                          key={tpl.key}
                          type="button"
                          onClick={() => handleTemplateSelect(tpl.key)}
                          className={cn(
                            'relative text-left px-4 py-3 rounded-xl transition-all duration-150',
                            selected
                              ? 'bg-primary/[0.08] ring-2 ring-primary shadow-sm'
                              : 'bg-muted/40 ring-1 ring-border hover:ring-muted-foreground/30 hover:bg-muted/60',
                          )}
                        >
                          {selected && (
                            <div className="absolute top-2.5 right-2.5 size-5 rounded-full bg-primary flex items-center justify-center">
                              <Check className="size-3 text-primary-foreground" />
                            </div>
                          )}
                          <div className="text-sm font-medium text-foreground leading-tight pr-5">{tpl.name}</div>
                          <div className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">{tpl.description}</div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <Separator />

                {/* Owner Prompt */}
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-foreground">Owner Prompt</label>
                    <p className="text-xs text-muted-foreground mt-0.5">Custom instructions that define this agent's behavior.</p>
                  </div>
                  <VariablesDisplay formData={formData} />
                  <textarea
                    className={cn(
                      'w-full min-h-[180px] px-4 py-3 border rounded-xl text-sm leading-relaxed focus:outline-none focus:ring-2 transition-colors bg-background resize-y',
                      promptPct >= 0.99
                        ? 'border-destructive focus:ring-destructive bg-destructive/5'
                        : promptPct >= 0.95
                          ? 'border-amber-400 focus:ring-amber-500 bg-amber-50/20 dark:bg-amber-950/20'
                          : 'border-input focus:ring-ring',
                    )}
                    placeholder="Write your agent's instructions here..."
                    value={(formData.owner_prompt as string) || ''}
                    onChange={e => {
                      const val = e.target.value
                      if (val.length > 10000) return
                      const tpl = (formData.prompt_template_key as string) || 'base_instructions'
                      if (tpl !== 'custom') updateFormField({ owner_prompt: val, prompt_template_key: 'custom' })
                      else updateFormField({ owner_prompt: val })
                    }}
                    maxLength={10000}
                  />
                  <div className="flex justify-between">
                    <p className="text-xs text-muted-foreground">Cannot override system safety rules.</p>
                    <span className={cn(
                      'text-xs font-medium tabular-nums',
                      promptPct >= 0.95 ? 'text-destructive' : promptPct >= 0.8 ? 'text-amber-600' : 'text-muted-foreground',
                    )}>
                      {promptLen.toLocaleString()}/10,000
                    </span>
                  </div>
                  {errors.owner_prompt && (
                    <div className="p-2.5 rounded-lg text-xs bg-destructive/10 text-destructive border border-destructive/20">
                      {errors.owner_prompt}
                    </div>
                  )}
                </div>

                {/* Style & Tone */}
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-foreground">Style & Tone</label>
                    <p className="text-xs text-muted-foreground mt-0.5">How the agent communicates with users.</p>
                  </div>
                  <textarea
                    className="w-full min-h-[100px] px-4 py-3 border border-input rounded-xl text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring bg-background resize-y"
                    placeholder="Define communication style..."
                    value={(formSettings?.style_tone_instructions as string) ?? DEFAULT_STYLE_TONE}
                    onChange={e => updateFormField({ settings: { ...formSettings, style_tone_instructions: e.target.value } })}
                    maxLength={2000}
                  />
                </div>
              </div>
            )}

            {activeSection === 'intelligence' && (
              <div className="space-y-6">
                {/* Search & Response */}
                <div className="rounded-xl border bg-card p-5 space-y-5">
                  <div>
                    <h3 className="text-md font-medium leading-none">Search & Response</h3>
                    <p className="text-sm text-muted-foreground mt-1">Control how your agent finds and generates answers.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">Search Sensitivity</label>
                      <p className="text-xs text-muted-foreground mb-1">How closely results must match the query.</p>
                      <Select
                        options={SEARCH_THRESHOLD_OPTIONS}
                        value={String((formData.search_threshold as number) ?? 0.5)}
                        onChange={v => updateFormField({ search_threshold: parseFloat(v) })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">AI Temperature</label>
                      <p className="text-xs text-muted-foreground mb-1">
                        {(formData.ai_model as string)?.startsWith('moonshotai/')
                          ? 'Kimi models require temperature 1.0'
                          : 'Controls response creativity vs consistency.'}
                      </p>
                      <Select
                        options={TEMPERATURE_OPTIONS}
                        value={String((formData.temperature as number) ?? 0.1)}
                        onChange={v => updateFormField({ temperature: parseFloat(v) })}
                        disabled={(formData.ai_model as string)?.startsWith('moonshotai/')}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">Knowledge Mode</label>
                      <p className="text-xs text-muted-foreground mb-1">How the agent uses its training data.</p>
                      <Select
                        options={KNOWLEDGE_MODE_OPTIONS}
                        value={(formSettings?.knowledge_mode as string) || 'grounded'}
                        onChange={v => updateFormField({ settings: { ...formSettings, knowledge_mode: v } })}
                      />
                    </div>
                  </div>
                </div>

                {/* Human Handoff */}
                <div className="rounded-xl border bg-card p-5 space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-md font-medium leading-none">Human Handoff</h3>
                      <p className="text-sm text-muted-foreground mt-1">Transfer conversations to live agents when AI can't help.</p>
                    </div>
                    <Switch
                      checked={(formData.allow_handoff as boolean) ?? false}
                      onCheckedChange={checked => updateFormField({ allow_handoff: checked })}
                    />
                  </div>

                  {(formData.allow_handoff as boolean) && (
                    <div className="space-y-1.5 pt-2 border-t">
                      <label className="text-sm font-medium text-foreground">Fallback Strategy</label>
                      <p className="text-xs text-muted-foreground mb-1">What happens when no good answer is found.</p>
                      <Select
                        options={FALLBACK_STRATEGY_OPTIONS}
                        value={(formData.fallback_strategy as string) || 'clarify_first'}
                        onChange={v => updateFormField({ fallback_strategy: v })}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'tools' && (
              <ToolsTab chatbot={chatbot} />
            )}

            {activeSection === 'skills' && (
              <SkillsTab chatbot={chatbot} />
            )}

            {activeSection === 'advanced' && (
              <div className="space-y-8">
                <MetadataConfig
                  value={(formData.metadata_schema as Record<string, unknown>) || { fields: [] }}
                  onChange={schema => updateFormField({ metadata_schema: schema })}
                />
                <Separator />
                <VariablesSection formData={formData} onChange={updateFormField} />
              </div>
            )}
          </div>

          {/* Save bar */}
          {isFormSection && (
            <div className="mt-8 pt-6 border-t border-border">
              {errors.submit && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                  <p className="text-sm text-destructive">{errors.submit}</p>
                </div>
              )}
              <div className="flex items-center justify-end">
                <Button
                  onClick={handleSubmit}
                  disabled={isUpdating}
                  size="lg"
                >
                  {isUpdating
                    ? <><Loader2 className="size-4 mr-2 animate-spin" />Saving...</>
                    : <><Save className="size-4 mr-2" />Save Changes</>
                  }
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
